import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { catchError, forkJoin, map, of } from 'rxjs';
import { buildInfo } from 'src/build-info';
import { environment } from 'src/environments/environment';
import { getRequestBaseUrl } from './common/utils/get-request-base-url';

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
    private http = inject(HttpClient);
    private platformId = inject(PLATFORM_ID);

    getTranslation(lang: string) {
        const baseUrl = getRequestBaseUrl(this.platformId);
        const cacheKey = encodeURIComponent(buildInfo.commitShortSha || buildInfo.version);

        return forkJoin([
            this.http.get<Translation>(`${baseUrl}/assets/i18n/${lang}.json?v=${cacheKey}`),
            this.http
                .get<Translation>(`${baseUrl}/assets/i18n/sellers/${environment.sellerId}/${lang}.json?v=${cacheKey}`)
                .pipe(catchError(() => of({} as Translation))),
        ]).pipe(map(([baseTranslation, sellerTranslation]) => deepMerge(baseTranslation, sellerTranslation)));
    }
}

function deepMerge(base: Translation, override: Translation): Translation {
    const result: Record<string, unknown> = { ...base };

    for (const [key, overrideValue] of Object.entries(override)) {
        const baseValue = result[key];

        if (isRecord(baseValue) && isRecord(overrideValue)) {
            result[key] = deepMerge(baseValue as Translation, overrideValue as Translation);
        } else {
            result[key] = overrideValue;
        }
    }

    return result as Translation;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
